/**
 * Accessibility Statement — chapter "ACCESSIBILITY" of the Trust & Support ask.
 * Wording deliberately reflects only what has actually been built.
 */
import { useNavigate } from "react-router-dom";
import { Accessibility, Keyboard, Palette, EyeOff, MonitorSmartphone, Ear, MessageSquare, ArrowLeft } from "lucide-react";

export default function AccessibilityStatement() {
  const navigate = useNavigate();
  return (
    <div className="max-w-3xl space-y-8" data-testid="a11y-page">
      <div>
        <button className="text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1" onClick={() => navigate("/help")}>
          <ArrowLeft className="h-3 w-3" /> Back to Help
        </button>
        <div className="mt-3 text-[10px] font-semibold tracking-[0.12em] uppercase text-slate-500">Accessibility</div>
        <h1 className="text-2xl font-heading font-semibold text-slate-900 tracking-tight mt-1">Accessibility statement</h1>
        <p className="text-sm text-slate-600 mt-2 leading-relaxed">
          SUNTRONIX ONE is designed with reference to <strong>WCAG 2.2 AA</strong> and <strong>ISO 9241-210</strong> human-centred design principles. This page is a plain summary of what the platform currently supports and what we are actively improving. We do not claim independent certification unless separately attested.
        </p>
      </div>

      <Section icon={Keyboard} title="Keyboard navigation">
        Every interactive control can be reached with <kbd className="rounded bg-slate-100 border border-slate-200 px-1 py-0.5 text-[10px] font-mono">Tab</kbd>. Focus rings are visible on all interactive elements. Modals and drawers trap focus and restore it on close.
      </Section>
      <Section icon={Palette} title="Colour & contrast">
        Text and interactive contrast is targeted to WCAG 2.2 AA. Status is never conveyed by colour alone — every badge carries a text label and, where relevant, an icon.
      </Section>
      <Section icon={EyeOff} title="Reduced motion">
        Motion is used sparingly to clarify state (loading, transitions). The interface respects the browser's <span className="font-mono text-[12px]">prefers-reduced-motion</span> setting.
      </Section>
      <Section icon={Ear} title="Screen reader semantics">
        Native HTML semantics are used wherever possible. ARIA is layered on top only when native semantics are insufficient (e.g. custom dropdowns and dialogs). All form fields carry persistent labels — placeholder text is never used as a substitute.
      </Section>
      <Section icon={MonitorSmartphone} title="Responsive & zoom">
        The application is designed to work from 375px width upwards. Zooming to 200% does not clip primary content.
      </Section>
      <Section icon={MessageSquare} title="Known limitations">
        <ul className="list-disc pl-4 mt-1 space-y-1">
          <li>Some complex data tables do not yet expose full column-reorder semantics for screen readers — remediation is planned.</li>
          <li>Dark-mode contrast tokens are still being finalised across every page.</li>
          <li>Charts do not yet include an accessible tabular alternative on every screen — planned.</li>
        </ul>
      </Section>

      <div className="bg-white border border-slate-200 rounded-md p-5">
        <div className="text-sm font-semibold text-slate-900">Report an accessibility issue</div>
        <p className="text-sm text-slate-600 mt-1 leading-relaxed">
          If you or a colleague run into any barrier while using SUNTRONIX ONE, please write to
          <a href="mailto:accessibility@suntronix.one" className="ml-1 underline text-slate-900">accessibility@suntronix.one</a>.
          We aim to acknowledge within two business days and provide a remediation plan.
        </p>
      </div>
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <div className="flex gap-3">
      <div className="h-9 w-9 rounded-md bg-slate-50 border border-slate-100 grid place-items-center shrink-0">
        <Icon className="h-4 w-4 text-slate-700" strokeWidth={1.6} />
      </div>
      <div className="min-w-0">
        <div className="text-sm font-heading font-semibold text-slate-900">{title}</div>
        <div className="text-sm text-slate-600 mt-1 leading-relaxed">{children}</div>
      </div>
    </div>
  );
}
